import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { filesApi } from '@/lib/api';
import { useToolStore } from '@/store';
import { generateEditorImage } from '@/constants/utils/editor_images';
import {
  getPreferredToolModel,
  getToolPickerModels,
} from '@/components/Tools/toolDetailHelpers';

const ACCENT = '#3157a4';
const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const IMAGE_TOOL_SLUG = 'image-generator';

const STYLE_OPTIONS = [
  ['photorealistic', 'Photorealistic'],
  ['illustration', 'Illustration'],
  ['digital art', 'Digital art'],
  ['3D render', '3D render'],
];
const RATIO_OPTIONS = [
  ['16:9', 'Landscape'],
  ['1:1', 'Square'],
  ['9:16', 'Portrait'],
];

export default function EditorImageDialog({
  isOpen,
  onClose,
  onInsert,
  defaultAspectRatio = '16:9',
  /** Prefill the generate prompt (e.g. existing image alt when regenerating). */
  initialPrompt = '',
  /** When true, skip the upload tab and title the dialog as a regenerate. */
  regenerate = false,
}) {
  const { colors } = useTheme();
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const fetchToolBySlug = useToolStore(state => state.fetchToolBySlug);
  const imageTool = useToolStore(state => state.toolsBySlug?.[IMAGE_TOOL_SLUG] || null);

  const [mode, setMode] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('photorealistic');
  const [aspectRatio, setAspectRatio] = useState(defaultAspectRatio);
  const [modelId, setModelId] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const pickerModels = useMemo(() => getToolPickerModels(imageTool), [imageTool]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!isOpen) return;
    fetchToolBySlug?.(IMAGE_TOOL_SLUG).catch(() => {});
  }, [isOpen, fetchToolBySlug]);

  // Cancels any in-flight generation as well as closing. The parent keeps this
  // dialog mounted and only toggles `isOpen`, so the unmount cleanup above
  // effectively never runs — this is the only thing that aborts a running job.
  const close = useCallback(() => {
    abortRef.current?.abort();
    setStatus('');
    setError('');
    onClose();
  }, [onClose]);

  // Reset transient state when reopening, so a previously abandoned run can't
  // leave the submit button stuck on "Generating image…".
  useEffect(() => {
    if (!isOpen) return;
    setStatus('');
    setError('');
    setMode('generate');
    setPrompt(String(initialPrompt || ''));
    setAspectRatio(defaultAspectRatio);
    const preferred = getPreferredToolModel(imageTool);
    setModelId(preferred?.id ? String(preferred.id) : '');
  }, [isOpen, initialPrompt, defaultAspectRatio, imageTool]);

  useEffect(() => {
    if (!isOpen || modelId || !pickerModels.length) return;
    const preferred = getPreferredToolModel(imageTool) || pickerModels[0];
    if (preferred?.id) setModelId(String(preferred.id));
  }, [isOpen, modelId, pickerModels, imageTool]);

  useEffect(() => {
    if (!isOpen) return undefined;
    // Must be `close`, not `onClose` — otherwise Escape leaves the request
    // running and it later inserts an image the user already cancelled.
    const onKeyDown = event => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const isBusy = !!status;
  const title = regenerate ? 'Regenerate image' : 'Insert image';
  const submitLabel = regenerate ? 'Generate and replace' : 'Generate and insert';

  const finish = file => {
    if (!file?.cdnPath) throw new Error('Image has no usable URL');
    onInsert(file);
    setPrompt('');
    setStatus('');
    setError('');
    onClose();
  };

  const upload = async event => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;
    // PDF and PPTX export embed only PNG/JPEG, so keep uploads aligned with
    // what will actually survive the export instead of silently dropping.
    if (!['image/png', 'image/jpeg'].includes(selected.type)) {
      setError('Choose a PNG or JPEG image so it also appears in exports.');
      return;
    }
    setError('');
    setStatus('Uploading image…');
    try {
      const result = await filesApi.uploadFile({ file: selected });
      finish(result.file || result.existingFile);
    } catch (err) {
      setError(err?.message || 'Image upload failed.');
      setStatus('');
    }
  };

  const generate = async () => {
    if (prompt.trim().length < 3) {
      setError('Describe the image you want to generate.');
      return;
    }
    setError('');
    setStatus('Starting image model…');
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      const file = await generateEditorImage({
        prompt,
        style,
        aspectRatio,
        modelId: modelId || null,
        signal: abortRef.current.signal,
        onStatus: next => setStatus(next === 'running' ? 'Generating image…' : 'Finishing image…'),
      });
      finish(file);
    } catch (err) {
      if (err?.name !== 'AbortError') setError(err?.message || 'Image generation failed.');
      setStatus('');
    }
  };

  const surface = colors.solid_color_primary;
  const raised = colors.solid_color_block;
  const border = colors.border_color_secondary;
  const fg = colors.text_color_primary;
  const fgMuted = colors.text_color_secondary;

  const fieldStyle = {
    boxSizing: 'border-box',
    border: `1px solid ${border}`,
    borderRadius: 9,
    padding: '9px 11px',
    background: raised,
    color: fg,
    font: `13px/1.5 ${FONT_STACK}`,
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
    color: fgMuted,
    marginBottom: 6,
    fontFamily: FONT_STACK,
  };

  const tabStyle = active => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    padding: '8px 0',
    border: 0,
    borderRadius: 8,
    background: active ? surface : 'transparent',
    color: active ? ACCENT : fgMuted,
    font: `600 12.5px/1 ${FONT_STACK}`,
    cursor: 'pointer',
    boxShadow: active ? '0 1px 4px rgba(15, 23, 42, 0.14)' : 'none',
    transition: 'background 0.12s ease, color 0.12s ease',
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={event => { if (event.target === event.currentTarget) close(); }}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 50,
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        background: 'rgba(0, 0, 0, 0.55)',
        fontFamily: FONT_STACK,
      }}
    >
      <div
        style={{
          width: 'min(460px, 100%)',
          borderRadius: 14,
          background: surface,
          color: fg,
          border: `1px solid ${border}`,
          boxShadow: '0 24px 70px rgba(0, 0, 0, 0.38)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${border}` }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: 'rgba(49, 87, 164, 0.14)' }}>
            <Icon name="image" size={15} color={ACCENT} />
          </div>
          <strong style={{ flex: 1, fontSize: 14.5, fontWeight: 700, color: fg }}>{title}</strong>
          <button
            onClick={close}
            aria-label="Close image dialog"
            style={{ display: 'grid', placeItems: 'center', width: 28, height: 28, border: 0, borderRadius: 7, background: 'transparent', cursor: 'pointer', color: fgMuted }}
            onMouseEnter={event => { event.currentTarget.style.background = 'rgba(128, 128, 128, 0.14)'; }}
            onMouseLeave={event => { event.currentTarget.style.background = 'transparent'; }}
          >
            <Icon name="xmark" size={14} color={fgMuted} />
          </button>
        </div>

        <div style={{ padding: 16 }}>
          {!regenerate ? (
            <div style={{ display: 'flex', gap: 3, padding: 3, marginBottom: 16, borderRadius: 10, background: raised, border: `1px solid ${border}` }}>
              <button onClick={() => setMode('generate')} aria-pressed={mode === 'generate'} style={tabStyle(mode === 'generate')}>
                <Icon name="wand-magic-sparkles" size={12} color={mode === 'generate' ? ACCENT : fgMuted} />
                Generate with AI
              </button>
              <button onClick={() => setMode('upload')} aria-pressed={mode === 'upload'} style={tabStyle(mode === 'upload')}>
                <Icon name="upload" size={12} color={mode === 'upload' ? ACCENT : fgMuted} />
                Upload image
              </button>
            </div>
          ) : null}

          {mode === 'generate' || regenerate ? (
            <>
              <label style={labelStyle} htmlFor="editor-image-prompt">Image description</label>
              <textarea
                id="editor-image-prompt"
                autoFocus
                value={prompt}
                onChange={event => setPrompt(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) generate();
                }}
                placeholder="A cinematic editorial illustration of…"
                maxLength={2000}
                style={{ ...fieldStyle, width: '100%', minHeight: 96, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle} htmlFor="editor-image-model">AI image model</label>
                  <select
                    id="editor-image-model"
                    value={modelId}
                    onChange={event => setModelId(event.target.value)}
                    disabled={!pickerModels.length}
                    style={{ ...fieldStyle, width: '100%', cursor: 'pointer' }}
                  >
                    {!pickerModels.length ? (
                      <option value="">Loading models…</option>
                    ) : (
                      pickerModels.map(model => (
                        <option key={model.id} value={String(model.id)}>
                          {model.name || model.displayName || model.slug || 'Image model'}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle} htmlFor="editor-image-style">Style</label>
                  <select id="editor-image-style" value={style} onChange={event => setStyle(event.target.value)} style={{ ...fieldStyle, width: '100%', cursor: 'pointer' }}>
                    {STYLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div style={{ width: 140 }}>
                  <label style={labelStyle} htmlFor="editor-image-ratio">Aspect ratio</label>
                  <select id="editor-image-ratio" value={aspectRatio} onChange={event => setAspectRatio(event.target.value)} style={{ ...fieldStyle, width: '100%', cursor: 'pointer' }}>
                    {RATIO_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 11.5, lineHeight: 1.4, color: fgMuted, fontFamily: FONT_STACK }}>
                Uses credits the same way as Tools → Image Generator for the selected model.
              </p>
              <button
                disabled={isBusy}
                onClick={generate}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  marginTop: 12,
                  padding: '11px 0',
                  border: 0,
                  borderRadius: 9,
                  background: ACCENT,
                  color: '#fff',
                  font: `700 13px/1 ${FONT_STACK}`,
                  cursor: isBusy ? 'default' : 'pointer',
                  opacity: isBusy ? 0.75 : 1,
                }}
              >
                <Icon name={isBusy ? 'spinner' : 'wand-magic-sparkles'} size={13} color="#fff" />
                {status || submitLabel}
              </button>
            </>
          ) : (
            <>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" onChange={upload} style={{ display: 'none' }} />
              <button
                disabled={isBusy}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 9,
                  width: '100%',
                  minHeight: 132,
                  border: `2px dashed ${border}`,
                  borderRadius: 11,
                  background: raised,
                  color: fg,
                  cursor: isBusy ? 'default' : 'pointer',
                  fontFamily: FONT_STACK,
                  transition: 'border-color 0.12s ease',
                }}
                onMouseEnter={event => { if (!isBusy) event.currentTarget.style.borderColor = ACCENT; }}
                onMouseLeave={event => { event.currentTarget.style.borderColor = border; }}
              >
                <div style={{ width: 38, height: 38, borderRadius: 19, display: 'grid', placeItems: 'center', background: 'rgba(49, 87, 164, 0.14)' }}>
                  <Icon name={isBusy ? 'spinner' : 'upload'} size={16} color={ACCENT} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {status || 'Choose an image from your computer'}
                </span>
                {!isBusy ? <span style={{ fontSize: 11.5, color: fgMuted }}>PNG or JPEG · exports keep these formats</span> : null}
              </button>
            </>
          )}

          {error ? (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 12,
                padding: '9px 11px',
                borderRadius: 8,
                background: 'rgba(220, 38, 38, 0.1)',
                color: '#dc2626',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: FONT_STACK,
              }}
            >
              <Icon name="triangle-exclamation" size={13} color="#dc2626" />
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
